// src/pages/Corporation.jsx
import React from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { PublicAPI } from '../api/public';

const Container = styled.div`
  .container { 
    width: min(1400px, 96vw); 
    margin: 0 auto; 
    padding: 0 18px; 
  }
`;

const VideoSection = styled.div`
  max-width: 1200px;
  margin: 60px auto;
  position: relative;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  background: #000;
  
  video {
    width: 100%;
    height: auto;
    display: block;
    max-height: 70vh;
    object-fit: cover;
  }
  
  @media (max-width: 768px) {
    margin: 40px auto;
    border-radius: 12px;
    
    video {
      max-height: 50vh;
    }
  }
`;

const Content = styled.div`
  padding: 40px 0;
  
  h1 {
    font-size: clamp(32px, 5vw, 48px);
    margin-bottom: 30px;
    color: var(--fg);
    text-align: center;
  }
  
  .intro {
    font-size: 20px;
    line-height: 1.6;
    color: var(--muted);
    margin-bottom: 50px;
    text-align: center;
    max-width: 800px;
    margin-left: auto;
    margin-right: auto;
  }
  
  .sections {
    display: grid;
    gap: 40px;
    margin: 50px 0;
  }
  
  .section {
    background: var(--card);
    border: 1px solid var(--bdr);
    border-radius: var(--radius);
    padding: 40px;
    box-shadow: var(--shadow);
    
    h2 {
      color: var(--primary);
      margin-bottom: 20px;
      font-size: 28px;
    }
    
    h3 {
      color: var(--fg);
      margin: 25px 0 15px 0;
      font-size: 22px;
    }
    
    p {
      color: var(--muted);
      line-height: 1.7;
      margin-bottom: 20px;
      font-size: 16px;
    }
    
    ul {
      list-style: none;
      padding: 0;
      margin: 20px 0;
      
      li {
        padding: 12px 0;
        color: var(--fg);
        position: relative;
        padding-left: 25px;
        line-height: 1.6;
        
        &:before {
          content: '▸';
          position: absolute;
          left: 0;
          color: var(--primary);
          font-weight: bold;
          font-size: 18px;
        }
      }
    }
    
    .highlight {
      background: rgba(176, 141, 87, 0.1);
      border-left: 4px solid var(--primary);
      padding: 20px;
      margin: 25px 0;
      border-radius: 8px;
      
      p {
        margin: 0;
        font-style: italic;
        color: var(--fg);
      }
    }
    
    .rich-content {
      h3 {
        color: var(--fg);
        margin: 25px 0 15px 0;
        font-size: 22px;
      }
      
      p {
        color: var(--muted);
        line-height: 1.7;
        margin-bottom: 20px;
        font-size: 16px;
      }
    }
  }
  
  @media (max-width: 768px) {
    padding: 20px 0;
    
    .section {
      padding: 25px 20px;
      
      h2 {
        font-size: 24px;
      }
      
      h3 {
        font-size: 20px;
      }
    }
  }
`;

const LoadingWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
  color: var(--muted);
`;

export default function Corporation() {
  const { t, i18n } = useTranslation();
  const [content, setContent] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);

  const currentLang = i18n.language || 'ru';
  const isRu = currentLang === 'ru';
  const isZh = currentLang === 'zh';

  // Вспомогательная функция для получения текста на нужном языке
  const getText = (data, field) => {
    if (isRu) return data[`${field}_ru`];
    if (isZh) return data[`${field}_zh`] || data[`${field}_en`];
    return data[`${field}_en`];
  };

  React.useEffect(() => {
    const loadContent = async () => {
      try {
        setLoading(true);
        // Пытаемся загрузить из админского API
        const data = await PublicAPI.getCorporationContent?.() || {};
        setContent(data);
      } catch (e) {
        console.error('Failed to load corporation content:', e);
        setError(e.message);
        // Используем дефолтные данные при ошибке
        setContent(getDefaultContent());
      } finally {
        setLoading(false);
      }
    };

    loadContent();
  }, []);

  const getDefaultContent = () => ({
    title_ru: "Корпорация АСТОРИУС",
    title_en: "ASTORIUS Corporation",
    intro_ru: "Мы — ведущая корпорация в сфере премиальной недвижимости, объединяющая экспертизу, инновации и безупречный сервис для создания исключительного опыта наших клиентов.",
    intro_en: "We are a leading corporation in the field of premium real estate, combining expertise, innovation and impeccable service to create an exceptional experience for our clients.",
    // ... остальные дефолтные данные
  });

  const renderList = (listText) => {
    if (!listText) return null;
    const items = listText.split('\n').filter(item => item.trim());
    return (
      <ul>
        {items.map((item, index) => (
          <li key={index}>{item.trim()}</li>
        ))}
      </ul>
    );
  };

  const renderRichContent = (htmlContent) => {
    if (!htmlContent) return null;
    return (
      <div 
        className="rich-content"
        dangerouslySetInnerHTML={{ __html: htmlContent }}
      />
    );
  };

  if (loading) {
    return (
      <Container>
        <div className="container">
          <LoadingWrapper>
            Загрузка...
          </LoadingWrapper>
        </div>
      </Container>
    );
  }

  if (error && !content) {
    return (
      <Container>
        <div className="container">
          <LoadingWrapper>
            Ошибка загрузки контента
          </LoadingWrapper>
        </div>
      </Container>
    );
  }

  const data = content || getDefaultContent();

  return (
    <Container>
      <div className="container">
        <Content>
          <h1>{getText(data, 'title')}</h1>
          
          <div className="intro">
            {getText(data, 'intro')}
          </div>

          <div className="sections">
            {/* О корпорации */}
            <div className="section">
              <h2>{getText(data, 'about_title')}</h2>
              {data.about_content_ru && (
                renderRichContent(getText(data, 'about_content'))
              )}
              
              {(data.mission_title_ru || data.mission_title_en) && (
                <>
                  <h3>{getText(data, 'mission_title')}</h3>
                  <p>{getText(data, 'mission_content')}</p>
                </>
              )}
              
              {(data.quote_ru || data.quote_en) && (
                <div className="highlight">
                  <p>"{getText(data, 'quote')}"</p>
                </div>
              )}
            </div>

            {/* Корпоративные ценности */}
            {(data.values_list_ru || data.values_list_en) && (
              <div className="section">
                <h2>{getText(data, 'values_title')}</h2>
                {renderList(getText(data, 'values_list'))}
              </div>
            )}

            {/* Направления деятельности */}
            {(data.directions_content_ru || data.directions_content_en) && (
              <div className="section">
                <h2>{getText(data, 'directions_title')}</h2>
                {renderRichContent(getText(data, 'directions_content'))}
              </div>
            )}

            {/* Корпоративная структура */}
            {(data.structure_content_ru || data.structure_content_en) && (
              <div className="section">
                <h2>{getText(data, 'structure_title')}</h2>
                <p>{getText(data, 'structure_content')}</p>
                {renderList(getText(data, 'structure_list'))}
              </div>
            )}

            {/* Достижения */}
            {(data.achievements_content_ru || data.achievements_content_en) && (
              <div className="section">
                <h2>{getText(data, 'achievements_title')}</h2>
                <p>{getText(data, 'achievements_content')}</p>
                
                {(data.achievements_highlight_ru || data.achievements_highlight_en) && (
                  <div className="highlight">
                    <p>{getText(data, 'achievements_highlight')}</p>
                  </div>
                )}
                
                {renderList(getText(data, 'achievements_list'))}
              </div>
            )}
          </div>
        </Content>
      </div>
    </Container>
  );
}